import {
  lazy,
  Suspense,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import {
  QueryClient,
  QueryClientProvider,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  BrowserRouter,
  Link,
  NavLink,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import {
  Anchor,
  ArrowDown,
  ArrowUp,
  BatteryMedium,
  BellRing,
  CircleCheck,
  CirclePower,
  Gauge,
  History,
  LayoutDashboard,
  Lightbulb,
  LocateFixed,
  LogOut,
  Menu,
  MessageSquareText,
  Moon,
  Plus,
  Radio,
  RefreshCw,
  Settings,
  Signal,
  Sun,
  Thermometer,
  Trash2,
  TriangleAlert,
  Users,
  Factory,
  Waves,
  X,
} from "lucide-react";
import { io } from "socket.io-client";
import { BuoyXApi } from "@buoy-x/api-client";
import type {
  Buoy,
  ConnectionStatus,
  CommandType,
  FirmwareStatus,
  ThemeMode,
  TrackPoint,
} from "@buoy-x/domain";
import { connectionStatusOf } from "@buoy-x/domain";
import { t } from "@buoy-x/i18n";
import { ThemeProvider, useTheme } from "@buoy-x/theme";
import { authClient } from "./auth";
import { runtimeConfig } from "./runtime";
const FactoryPanel = lazy(() => import("./factory/FactoryPanel.js"));

const ClaimDialog = lazy(() =>
  import("./ClaimDialog").then((module) => ({ default: module.ClaimDialog })),
);
const MapPanel = lazy(() =>
  import("./MapPanel").then((module) => ({ default: module.MapPanel })),
);

const api = new BuoyXApi(runtimeConfig.apiUrl);
const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 10_000, retry: 1, refetchOnWindowFocus: false },
  },
});

type AppUser = {
  id: string;
  name: string;
  email: string;
  emailVerified: boolean;
  role?: string;
  themeMode?: ThemeMode;
};

export function BuoyXWebApp({ admin = false }: { admin?: boolean }) {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider
        onChange={(mode) => void api.saveTheme(mode).catch(() => undefined)}
      >
        <BrowserRouter>
          <AuthBoundary admin={admin} />
        </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

function AuthBoundary({ admin }: { admin: boolean }) {
  const session = authClient.useSession();
  if (session.isPending) return <Splash />;
  const user = session.data?.user as AppUser | undefined;
  if (!user) return <AuthPage />;
  if (admin && user.role !== "admin") return <AccessDenied />;
  return <Shell user={user} admin={admin} />;
}

function Splash() {
  return (
    <main className="splash">
      <img src="/logo.png" alt="" />
      <span>BUOY X</span>
      <div className="loading-line" />
    </main>
  );
}

function AccessDenied() {
  return (
    <main className="center-card">
      <Anchor size={42} />
      <h1>Admin access required</h1>
      <p>This account cannot open the fleet administration console.</p>
      <button
        className="primary-button"
        onClick={() => void authClient.signOut()}
      >
        Sign out
      </button>
    </main>
  );
}

function AuthPage() {
  const { mode, setMode } = useTheme();
  const [register, setRegister] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    setMessage("");
    try {
      if (register) {
        const result = await authClient.signUp.email({ name, email, password });
        if (result.error) throw new Error(result.error.message);
        setMessage(t("verifiedHint"));
      } else {
        const result = await authClient.signIn.email({ email, password });
        if (result.error) throw new Error(result.error.message);
      }
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Authentication failed",
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="auth-layout">
      <section className="auth-panel">
        <div className="auth-theme-picker" aria-label="Appearance">
          {(["system", "light", "dark"] as ThemeMode[]).map((value) => (
            <button
              key={value}
              aria-label={`Use ${value} appearance`}
              title={`${value} appearance`}
              className={mode === value ? "active" : ""}
              onClick={() => setMode(value)}
            >
              {value === "system" ? (
                <Settings />
              ) : value === "light" ? (
                <Sun />
              ) : (
                <Moon />
              )}
            </button>
          ))}
        </div>
        <div className="auth-card">
          <img className="auth-logo" src="/logo.png" alt="Buoy X" />
          <h2>{register ? t("createAccount") : t("signIn")}</h2>
          {register && (
            <p className="muted auth-hint">
              Create your account, then verify your email.
            </p>
          )}
          <form className="form-stack" onSubmit={submit}>
            {register && (
              <label>
                {t("name")}
                <input
                  required
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  autoComplete="name"
                />
              </label>
            )}
            <label>
              {t("email")}
              <input
                required
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="email"
              />
            </label>
            <label>
              {t("password")}
              <input
                required
                minLength={10}
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete={register ? "new-password" : "current-password"}
              />
            </label>
            <button className="primary-button" disabled={busy}>
              {busy
                ? "Please wait…"
                : register
                  ? t("createAccount")
                  : t("signIn")}
            </button>
          </form>
          <div className="separator">
            <span>or</span>
          </div>
          <div className="social-row">
            <button
              onClick={() =>
                void authClient.signIn.social({
                  provider: "google",
                  callbackURL: window.location.href,
                })
              }
            >
              G <span>{t("continueGoogle")}</span>
            </button>
            <button
              onClick={() =>
                void authClient.signIn.social({
                  provider: "apple",
                  callbackURL: window.location.href,
                })
              }
            >
              ● <span>{t("continueApple")}</span>
            </button>
          </div>
          {error && (
            <p className="error" role="alert">
              {error}
            </p>
          )}
          {message && (
            <p className="success" role="status">
              {message}
            </p>
          )}
          <button
            className="text-button"
            onClick={() => setRegister(!register)}
          >
            {register
              ? "Already have an account? Sign in"
              : "New to Buoy X? Create an account"}
          </button>
        </div>
      </section>
    </main>
  );
}

function Shell({ user, admin }: { user: AppUser; admin: boolean }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const client = useQueryClient();
  const { syncMode } = useTheme();
  useEffect(() => {
    if (user.themeMode) syncMode(user.themeMode);
  }, [syncMode, user.themeMode]);
  useEffect(() => {
    const socket = io(`${runtimeConfig.apiUrl.replace(/\/$/, "")}/realtime`, {
      withCredentials: true,
      transports: ["websocket"],
    });
    const refresh = () => {
      void client.invalidateQueries({ queryKey: ["buoys"] });
      void client.invalidateQueries({ queryKey: ["buoy"] });
    };
    const refreshCommand = () => {
      refresh();
      void client.invalidateQueries({ queryKey: ["commands"] });
    };
    socket.on("buoy.state", refresh);
    socket.on("buoy.telemetry", refresh);
    socket.on("command.status", refreshCommand);
    return () => {
      socket.disconnect();
    };
  }, [client]);
  return (
    <div className="app-shell">
      <header className="mobile-header">
        <Brand />
        <button
          className="icon-button"
          aria-label="Menu"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          {menuOpen ? <X /> : <Menu />}
        </button>
      </header>
      <aside className={menuOpen ? "sidebar open" : "sidebar"}>
        <div className="sidebar-heading">
          <Brand />
          <span>{admin ? "Administration" : "Fleet command"}</span>
        </div>
        <nav onClick={() => setMenuOpen(false)}>
          <NavItem
            to="/"
            icon={<LayoutDashboard />}
            label={admin ? t("allBuoys") : t("fleet")}
          />
          {admin && <NavItem to="/users" icon={<Users />} label={t("users")} />}
          {admin && <NavItem to="/audit" icon={<History />} label="Audit" />}
          {admin && (
            <NavItem to="/factory" icon={<Factory />} label={t("factory")} />
          )}
          <NavItem to="/account" icon={<Settings />} label={t("account")} />
        </nav>
        <div className="sidebar-user">
          <div className="avatar">{user.name.slice(0, 1).toUpperCase()}</div>
          <div>
            <strong>{user.name}</strong>
            <span>{user.email}</span>
          </div>
          <button
            className="icon-button"
            title={t("signOut")}
            onClick={() => void authClient.signOut()}
          >
            <LogOut />
          </button>
        </div>
      </aside>
      <main className="main-content">
        <Routes>
          <Route path="/" element={<Fleet admin={admin} user={user} />} />
          <Route path="/buoys/:id" element={<BuoyDetail admin={admin} />} />
          <Route
            path="/claim"
            element={<Fleet admin={admin} user={user} openClaim />}
          />
          <Route path="/account" element={<AccountPage user={user} />} />
          {admin && <Route path="/users" element={<UsersPage />} />}
          {admin && <Route path="/audit" element={<AuditPage />} />}
          {admin && (
            <Route
              path="/factory"
              element={
                <Suspense fallback={<Splash />}>
                  <FactoryPanel />
                </Suspense>
              }
            />
          )}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

function Brand() {
  return (
    <Link className="brand-lockup sidebar-brand" to="/">
      <img src="/logo.png" alt="" />
      <span>BUOY X</span>
    </Link>
  );
}
function NavItem({
  to,
  icon,
  label,
}: {
  to: string;
  icon: ReactNode;
  label: string;
}) {
  return (
    <NavLink to={to} end={to === "/"}>
      {icon}
      <span>{label}</span>
    </NavLink>
  );
}

function Fleet({
  admin,
  user,
  openClaim = false,
}: {
  admin: boolean;
  user: AppUser;
  openClaim?: boolean;
}) {
  const [claimOpen, setClaimOpen] = useState(openClaim);
  const [search, setSearch] = useState("");
  const location = useLocation();
  const claimParameters = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      serial: params.get("serial") ?? "",
      code: params.get("code") ?? "",
    };
  }, [location.search]);
  const client = useQueryClient();
  const query = useQuery({
    queryKey: ["buoys", admin, search],
    queryFn: () => (admin ? api.adminBuoys(search) : api.listBuoys()),
    refetchInterval: 30_000,
  });
  const buoys = query.data?.items ?? [];
  return (
    <>
      <PageHeader
        eyebrow={
          admin
            ? "Fleet administration"
            : `Good to see you, ${user.name.split(" ")[0]}`
        }
        title={admin ? "All connected buoys" : "Your buoys"}
        action={
          admin ? (
            <input
              className="search-input"
              aria-label="Search buoys"
              placeholder="Search serial, device, SIM, IMEI, or model"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          ) : (
            <button
              className="primary-button compact"
              onClick={() => setClaimOpen(true)}
            >
              <Plus /> {t("addBuoy")}
            </button>
          )
        }
      />
      {!user.emailVerified && !admin && (
        <div className="notice warning">
          <span>!</span>
          <div>
            <strong>Verify your email</strong>
            <p>{t("verifiedHint")}</p>
          </div>
        </div>
      )}
      {query.isLoading ? (
        <CardGridSkeleton />
      ) : query.isError ? (
        <Empty
          icon={<Radio />}
          title="Fleet unavailable"
          detail="The service could not be reached. Try again shortly."
        />
      ) : buoys.length === 0 ? (
        <Empty
          icon={<Waves />}
          title={t("noBuoys")}
          detail={
            admin ? "Provisioned buoys will appear here." : t("noBuoysHint")
          }
          action={
            !admin ? (
              <button
                className="primary-button"
                onClick={() => setClaimOpen(true)}
              >
                <Plus /> {t("addBuoy")}
              </button>
            ) : undefined
          }
        />
      ) : (
        <section className="buoy-grid" aria-label="Buoy list">
          {buoys.map((buoy) => (
            <BuoyCard key={buoy.id} buoy={buoy} />
          ))}
        </section>
      )}
      {claimOpen && (
        <Suspense fallback={null}>
          <ClaimDialog
            api={api}
            initialSerial={claimParameters.serial}
            initialCode={claimParameters.code}
            close={() => setClaimOpen(false)}
            claimed={() =>
              void client.invalidateQueries({ queryKey: ["buoys"] })
            }
          />
        </Suspense>
      )}
    </>
  );
}

function PageHeader({
  eyebrow,
  title,
  action,
}: {
  eyebrow: string;
  title: string;
  action?: ReactNode;
}) {
  return (
    <header className="page-header">
      <div>
        <span className="eyebrow">{eyebrow}</span>
        <h1>{title}</h1>
      </div>
      {action}
    </header>
  );
}

function StatusPill({ status }: { status: ConnectionStatus }) {
  return (
    <span className={`status-pill ${status}`}>
      <span className="status-dot" />
      {t(status)}
    </span>
  );
}

function BuoyCard({ buoy }: { buoy: Buoy }) {
  const state = buoy.state;
  const connectionStatus = connectionStatusOf(state);
  const hasKnownPosition = state?.latitude != null && state.longitude != null;
  return (
    <Link className="buoy-card" to={`/buoys/${buoy.id}`}>
      <div className="card-top">
        <div className="buoy-icon">
          <Anchor />
        </div>
        <StatusPill status={connectionStatus} />
      </div>
      <div>
        <span className="serial">{buoy.serial}</span>
        <h2>{buoy.name}</h2>
      </div>
      <div className="metric-row">
        <Metric
          icon={<BatteryMedium />}
          label={t("battery")}
          value={state?.batteryPct == null ? "—" : `${state.batteryPct}%`}
        />
        <Metric
          icon={<Signal />}
          label={t("signal")}
          value={state?.signalDbm == null ? "—" : `${state.signalDbm} dBm`}
        />
        <Metric
          icon={<LocateFixed />}
          label="GPS"
          value={
            state?.currentLocationKnown
              ? state.satellitesVisible == null
                ? "Fixed"
                : `${state.satellitesVisible} sat`
              : hasKnownPosition
                ? "Last known"
                : "Unknown"
          }
        />
      </div>
      <span className="last-seen">
        {state?.lastSeenAt
          ? `Updated ${formatRelative(state.lastSeenAt)}`
          : "Never connected"}
      </span>
    </Link>
  );
}

function Metric({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="metric">
      {icon}
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function BuoyDetail({ admin }: { admin: boolean }) {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const client = useQueryClient();
  const buoyQuery = useQuery({
    queryKey: ["buoy", id],
    queryFn: () => api.getBuoy(id),
    enabled: Boolean(id),
    refetchInterval: 30_000,
  });
  const commandsQuery = useQuery({
    queryKey: ["commands", id],
    queryFn: () => api.commands(id),
    enabled: Boolean(id) && admin,
    refetchInterval: 15_000,
  });
  const firmwareQuery = useQuery({
    queryKey: ["firmware", id],
    queryFn: () => api.firmwareStatus(id),
    enabled: Boolean(id),
    refetchInterval: (query) => {
      const phase = query.state.data?.update?.phase;
      return phase === "sending" || phase === "installing" ? 3_000 : false;
    },
  });
  const firmwareUpdate = useMutation({
    mutationFn: () => api.installFirmwareUpdate(id),
    onSuccess: (command) => {
      client.setQueryData<FirmwareStatus>(["firmware", id], (current) =>
        current?.availableVersion
          ? {
              ...current,
              canInstall: false,
              update: {
                commandId: command.id,
                targetVersion: current.availableVersion,
                phase: "sending",
                startedAt: new Date().toISOString(),
                errorCode: null,
              },
            }
          : current,
      );
      void client.invalidateQueries({ queryKey: ["commands", id] });
      void client.invalidateQueries({ queryKey: ["firmware", id] });
    },
  });
  const command = useMutation({
    mutationFn: ({
      type,
      payload,
    }: {
      type: CommandType;
      payload?: Record<string, unknown>;
    }) => api.command(id, type, payload),
    onSuccess: () =>
      void client.invalidateQueries({ queryKey: ["commands", id] }),
  });
  const [tab, setTab] = useState<
    "control" | "map" | "status" | "feedback" | "settings"
  >("control");
  const [buoyName, setBuoyName] = useState("");
  const buoy = buoyQuery.data;
  useEffect(() => {
    if (buoy?.name) setBuoyName(buoy.name);
  }, [buoy?.name]);
  const renameBuoy = useMutation({
    mutationFn: () => api.renameBuoy(id, buoyName.trim()),
    onSuccess: (updated) => {
      client.setQueryData(["buoy", id], updated);
      void client.invalidateQueries({ queryKey: ["buoys"] });
    },
  });
  const removeBuoy = useMutation({
    mutationFn: () => api.removeBuoy(id),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["buoys"] });
      navigate("/", { replace: true });
    },
  });
  if (buoyQuery.isLoading) return <CardGridSkeleton />;
  if (!buoy)
    return (
      <Empty
        icon={<Anchor />}
        title="Buoy not found"
        detail="It may have been removed from your account or you no longer have access."
        action={<button onClick={() => navigate("/")}>Back</button>}
      />
    );
  const state = buoy.state;
  const batteryDetail = [
    state?.batteryVoltageMv == null
      ? null
      : `${(state.batteryVoltageMv / 1000).toFixed(3)} V`,
    state?.batteryRawSoc == null ? null : `Raw SOC ${state.batteryRawSoc}%`,
    state?.batteryAdcVoltageMv == null
      ? null
      : `ADC ${(state.batteryAdcVoltageMv / 1000).toFixed(3)} V`,
  ]
    .filter(Boolean)
    .join(" · ") || "Device power";
  const connectionStatus = connectionStatusOf(state);
  const connectionDetail =
    connectionStatus === "online"
      ? "Connected"
      : connectionStatus === "sleep"
        ? "Automatic sleep"
        : "Offline";
  const hasPosition = state?.latitude != null && state.longitude != null;
  const currentLocationKnown = state?.currentLocationKnown ?? false;
  const position = latestPoint(state);
  const gpsDetail = currentLocationKnown
    ? state?.satellitesVisible != null
      ? `${state.satellitesVisible} satellites visible`
      : state?.accuracyM == null
        ? "Position available"
        : `${state.accuracyM} m accuracy`
    : hasPosition
      ? `Last fix ${formatRelative(state?.lastGpsFixAt ?? state?.updatedAt ?? "")}`
      : state?.lastGpsAttemptAt
        ? `No fix ${formatRelative(state.lastGpsAttemptAt)}`
        : "No known position";
  const firmware = firmwareQuery.data;
  const update = firmware?.update;
  const updateInProgress =
    update?.phase === "sending" || update?.phase === "installing";
  const updateTitle =
    update?.phase === "sending"
      ? `Sending firmware ${update.targetVersion}`
      : update?.phase === "installing"
        ? `Updating firmware to ${update.targetVersion}`
        : update?.phase === "succeeded"
          ? `Firmware ${update.targetVersion} installed`
          : update?.phase === "failed"
            ? `Firmware ${update.targetVersion} was not installed`
            : `Firmware ${firmware?.availableVersion} is available`;
  const updateDetail =
    update?.phase === "sending"
      ? "Waiting for the buoy to accept the update command."
      : update?.phase === "installing"
        ? "The buoy is downloading and verifying the update. It may appear offline while it restarts."
        : update?.phase === "succeeded"
          ? "The buoy restarted and checked in on the new version."
          : update?.phase === "failed"
            ? `The buoy did not check in on the requested version. It remains on ${firmware?.currentVersion ?? "its previous firmware"}.`
            : firmware?.supportsRemoteUpdate
              ? `Current version ${firmware.currentVersion ?? "unknown"}. The buoy will briefly disconnect while installing and will restore the previous firmware if the new version cannot check in.`
              : "This installed version needs one final USB update before future releases can be installed remotely.";
  const send = (type: CommandType, payload?: Record<string, unknown>) =>
    command.mutate({ type, payload });
  return (
    <div className="buoy-detail-page">
      <button className="back-button" onClick={() => navigate("/")}>
        &larr; {t("back")}
      </button>
      <header className="detail-header card">
        <div className="detail-identity">
          <div className="title-with-status">
            <div className="buoy-icon large">
              <Anchor />
            </div>
            <div>
              <span className="serial">{buoy.serial}</span>
              <h1>{buoy.name}</h1>
            </div>
          </div>
          <StatusPill status={connectionStatus} />
        </div>
        <div className="detail-vitals" aria-label="Current buoy status">
          <QuickStatus
            icon={<BatteryMedium />}
            label="Battery"
            value={state?.batteryPct == null ? "—" : `${state.batteryPct}%`}
            detail={batteryDetail}
          />
          <QuickStatus
            icon={<Signal />}
            label="LTE signal"
            value={state?.signalDbm == null ? "—" : `${state.signalDbm} dBm`}
            detail="Cellular link"
          />
          <QuickStatus
            icon={<LocateFixed />}
            label="GPS"
            value={currentLocationKnown ? "Fixed" : "Unknown"}
            detail={gpsDetail}
          />
          <QuickStatus
            icon={<Radio />}
            label="Last seen"
            value={
              state?.lastSeenAt ? formatRelative(state.lastSeenAt) : "Never"
            }
            detail={connectionDetail}
          />
        </div>
      </header>
      {firmware && (firmware.available || update) && (
        <section
          className={`firmware-update-banner${update ? ` firmware-update-${update.phase}` : ""}`}
          aria-live="polite"
        >
          <div className="firmware-update-icon">
            {update?.phase === "succeeded" ? (
              <CircleCheck />
            ) : update?.phase === "failed" ? (
              <TriangleAlert />
            ) : (
              <RefreshCw
                className={updateInProgress ? "firmware-update-spin" : ""}
              />
            )}
          </div>
          <div className="firmware-update-copy">
            <strong>{updateTitle}</strong>
            <span>{updateDetail}</span>
          </div>
          {firmware.available && !updateInProgress && (
            <button
              className="primary-button"
              disabled={
                !firmware.canInstall ||
                !state?.online ||
                firmwareUpdate.isPending
              }
              onClick={() => {
                if (
                  window.confirm(
                    `Install firmware ${firmware.availableVersion}? The buoy will disconnect and restart.`,
                  )
                )
                  firmwareUpdate.mutate();
              }}
            >
              {firmwareUpdate.isPending
                ? "Sending…"
                : !firmware.supportsRemoteUpdate
                  ? "USB update required"
                  : state?.online
                    ? update?.phase === "failed"
                      ? "Retry update"
                      : "Install update"
                    : "Buoy must be online"}
            </button>
          )}
          {firmwareUpdate.isError && (
            <span className="error">{firmwareUpdate.error.message}</span>
          )}
        </section>
      )}
      <nav className="detail-tabs">
        <Tab
          active={tab === "control"}
          onClick={() => setTab("control")}
          icon={<Gauge />}
          label={t("control")}
        />
        <Tab
          active={tab === "map"}
          onClick={() => setTab("map")}
          icon={<LocateFixed />}
          label={t("map")}
        />
        <Tab
          active={tab === "status"}
          onClick={() => setTab("status")}
          icon={<Radio />}
          label={t("status")}
        />
        {admin && (
          <Tab
            active={tab === "feedback"}
            onClick={() => setTab("feedback")}
            icon={<MessageSquareText />}
            label="Feedback"
          />
        )}
        <Tab
          active={tab === "settings"}
          onClick={() => setTab("settings")}
          icon={<Settings />}
          label={t("settings")}
        />
      </nav>
      {tab === "control" && (
        <section className="control-layout">
          <div className="control-hero card">
            <div className="control-hero-heading">
              <div>
                <span className="eyebrow">Anchor control</span>
                <h2>Position the buoy</h2>
              </div>
              <span
                className={`command-ready ${state?.online ? "online" : ""}`}
              >
                {state?.online
                  ? "Ready for command"
                  : connectionStatus === "sleep"
                    ? "Buoy sleeping"
                    : "Waiting for connection"}
              </span>
            </div>
            <div className="vertical-controls">
              <button
                className="raise-control"
                disabled={!state?.online || command.isPending}
                onClick={() => send("UP")}
              >
                <ArrowUp />
                <strong>Raise</strong>
                <span>Bring buoy up</span>
              </button>
              <div className="control-axis" aria-hidden="true">
                <span />
                <Anchor />
                <span />
              </div>
              <button
                className="lower-control"
                disabled={!state?.online || command.isPending}
                onClick={() => send("DOWN")}
              >
                <ArrowDown />
                <strong>Lower</strong>
                <span>Send buoy down</span>
              </button>
            </div>
          </div>
          <div className="control-side">
            <div className="systems-heading">
              <span className="eyebrow">On-board systems</span>
              <h2>Quick controls</h2>
            </div>
            <ControlGroup
              title="Navigation light"
              icon={<Lightbulb />}
              options={["OFF", "ON", "AUTO"]}
              onPick={(value) => send(`LIGHT_${value}` as CommandType)}
              disabled={!state?.online}
            />
            <ControlGroup
              title="Locator buzzer"
              icon={<BellRing />}
              options={["OFF", "ON", "AUTO"]}
              onPick={(value) => send(`BUZZER_${value}` as CommandType)}
              disabled={!state?.online}
            />
            <div className="button-grid">
              <button
                className="secondary-button"
                disabled={!state?.online}
                onClick={() => send("GPS_LOCATION")}
              >
                <LocateFixed />
                <span>
                  <strong>{t("gpsLocation")}</strong>
                  <small>Request the buoy’s current position</small>
                </span>
              </button>
              <button
                className="secondary-button"
                disabled={!state?.online}
                onClick={() => send("STATUS_REFRESH")}
              >
                <RefreshCw />
                <span>
                  <strong>{t("refresh")}</strong>
                  <small>Request current device readings</small>
                </span>
              </button>
              <button
                className="danger-button"
                disabled={!state?.online}
                onClick={() =>
                  confirm(
                    "Power off this buoy? It cannot be woken remotely in v1.",
                  ) && send("POWER_OFF")
                }
              >
                <CirclePower />
                <span>
                  <strong>{t("powerOff")}</strong>
                  <small>Remote wake is unavailable</small>
                </span>
              </button>
            </div>
            {command.isError && (
              <p className="error">{command.error.message}</p>
            )}
            {command.isSuccess && (
              <p className="success">
                Command queued. Waiting for acknowledgement.
              </p>
            )}
          </div>
        </section>
      )}
      {admin && tab === "feedback" && (
        <section className="card table-card command-history">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Device feedback</span>
              <h2>Recent commands</h2>
            </div>
            <History />
          </div>
          <table>
            <thead>
              <tr>
                <th>Command</th>
                <th>Status</th>
                <th>Requested</th>
                <th>Result</th>
              </tr>
            </thead>
            <tbody>
              {commandsQuery.data?.items.slice(0, 10).map((item) => (
                <tr key={item.id}>
                  <td>
                    <code>{item.type}</code>
                  </td>
                  <td>
                    <span className={`command-state ${item.status}`}>
                      {item.status === "acknowledged"
                        ? "✓"
                        : item.status === "failed" ||
                            item.status === "rejected" ||
                            item.status === "expired"
                          ? "!"
                          : "•"}{" "}
                      {item.status}
                    </span>
                  </td>
                  <td>{new Date(item.createdAt).toLocaleString()}</td>
                  <td>{item.errorCode ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!commandsQuery.isLoading && !commandsQuery.data?.items.length && (
            <p className="muted">No commands have been sent yet.</p>
          )}
        </section>
      )}
      {tab === "map" && (
        <section className="card map-card">
          <div className="section-heading">
            <div>
              <span className="eyebrow">GPS location</span>
              <h2>Last known position</h2>
              <p className="map-coordinates">
                {position
                  ? `Longitude ${position.longitude.toFixed(6)}, latitude ${position.latitude.toFixed(6)}, satellites visible ${position.satellitesVisible ?? "—"}. Fix recorded ${formatRelative(position.recordedAt)}.`
                  : state?.lastGpsAttemptAt
                    ? `No last known location. The latest attempt finished without a fix ${formatRelative(state.lastGpsAttemptAt)}.`
                    : "No last known location. The buoy has not reported a GPS result yet."}
              </p>
            </div>
            <LocateFixed />
          </div>
          <Suspense fallback={<div className="map-empty">Loading map…</div>}>
            <MapPanel point={position} />
          </Suspense>
        </section>
      )}
      {tab === "status" && (
        <section className="card status-console">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Device instrumentation</span>
              <h2>Current readings</h2>
            </div>
            <StatusPill status={connectionStatus} />
          </div>
          <div className="status-grid">
            <StatusCard
              icon={<BatteryMedium />}
              label="Battery"
              value={state?.batteryPct == null ? "—" : `${state.batteryPct}%`}
              detail={batteryDetail}
            />
            <StatusCard
              icon={<Signal />}
              label="LTE signal"
              value={state?.signalDbm == null ? "—" : `${state.signalDbm} dBm`}
              detail="Cellular modem"
            />
            <StatusCard
              icon={<LocateFixed />}
              label="GPS satellites"
              value={
                state?.satellitesVisible == null
                  ? "—"
                  : `${state.satellitesVisible} visible`
              }
              detail={
                currentLocationKnown
                  ? state?.accuracyM == null
                    ? "Current position fix"
                    : `${state.accuracyM} m accuracy`
                  : hasPosition
                    ? "From last known fix"
                    : "No known fix"
              }
            />
            <StatusCard
              icon={<CircleCheck />}
              label="Upper end switch"
              value={
                state?.endSwitchClosed == null
                  ? "Unknown"
                  : state.endSwitchClosed
                    ? "Closed"
                    : "Open"
              }
              detail="Upper travel limit"
            />
            <StatusCard
              icon={<Thermometer />}
              label="Motor temperature"
              value={
                state?.motorTemperatureCentiC == null
                  ? "—"
                  : `${(state.motorTemperatureCentiC / 100).toFixed(1)} °C`
              }
              detail="Motor sensor"
            />
            <StatusCard
              icon={<Thermometer />}
              label="Buoy temperature"
              value={
                state?.buoyTemperatureCentiC == null
                  ? "—"
                  : `${(state.buoyTemperatureCentiC / 100).toFixed(1)} °C`
              }
              detail="Inside buoy"
            />
          </div>
          <div className="device-signature">
            <span>
              Model <strong>{buoy.model}</strong>
            </span>
            <span>
              Firmware <strong>{buoy.firmwareVersion ?? "Not reported"}</strong>
            </span>
            <span>
              Device <strong>{buoy.deviceId}</strong>
            </span>
          </div>
        </section>
      )}
      {tab === "settings" && (
        <section className="settings-layout">
          <div className="card settings-card">
            <span className="eyebrow">Identity & hardware</span>
            <h2>Buoy settings</h2>
            <form
              className="buoy-name-form"
              onSubmit={(event) => {
                event.preventDefault();
                renameBuoy.mutate();
              }}
            >
              <label htmlFor="buoy-name">Display name</label>
              <p className="muted">The name shown throughout your fleet.</p>
              <div>
                <input
                  id="buoy-name"
                  required
                  maxLength={80}
                  value={buoyName}
                  onChange={(event) => setBuoyName(event.target.value)}
                  placeholder={buoy.model}
                />
                <button
                  className="primary-button"
                  disabled={
                    renameBuoy.isPending ||
                    !buoyName.trim() ||
                    buoyName.trim() === buoy.name
                  }
                >
                  {renameBuoy.isPending ? "Saving…" : "Save name"}
                </button>
              </div>
            </form>
            {renameBuoy.isSuccess && (
              <p className="success">Buoy name updated.</p>
            )}
            {renameBuoy.isError && (
              <p className="error">{renameBuoy.error.message}</p>
            )}
            <dl>
              <div>
                <dt>Serial number</dt>
                <dd>{buoy.serial}</dd>
              </div>
              <div>
                <dt>Device ID</dt>
                <dd>{buoy.deviceId}</dd>
              </div>
              <div>
                <dt>Firmware</dt>
                <dd>{buoy.firmwareVersion ?? "Not reported"}</dd>
              </div>
              <div>
                <dt>Lifecycle</dt>
                <dd>{buoy.lifecycle}</dd>
              </div>
              {admin && (
                <>
                  <div>
                    <dt>ESP32 chip MAC</dt>
                    <dd>{buoy.factory?.chipMac ?? "Not recorded"}</dd>
                  </div>
                  <div>
                    <dt>Modem IMEI</dt>
                    <dd>{buoy.factory?.modemImei ?? "Not recorded"}</dd>
                  </div>
                  <div>
                    <dt>SIM ICCID</dt>
                    <dd>{buoy.sim?.iccid ?? "Not recorded"}</dd>
                  </div>
                  <div>
                    <dt>SIM IMSI</dt>
                    <dd>{buoy.sim?.imsi ?? "Not available"}</dd>
                  </div>
                  <div>
                    <dt>SIM status</dt>
                    <dd>{buoy.sim?.status ?? "Not recorded"}</dd>
                  </div>
                  <div>
                    <dt>SIM provider</dt>
                    <dd>{buoy.sim?.provider ?? "Not available"}</dd>
                  </div>
                  <div>
                    <dt>SIM phone number</dt>
                    <dd>{buoy.sim?.msisdn ?? "Not stored on SIM"}</dd>
                  </div>
                  <div>
                    <dt>Current cellular network</dt>
                    <dd>{buoy.sim?.operator ?? "Not registered"}</dd>
                  </div>
                </>
              )}
            </dl>
          </div>
          <aside className="settings-side">
            {admin && (
              <div className="card privileged-card">
                <AdminActions buoy={buoy} />
              </div>
            )}
            {!admin && (
              <div className="card remove-card">
                <div className="remove-card-icon">
                  <Trash2 />
                </div>
                <h3>Remove buoy</h3>
                <p className="muted">
                  Unassign this buoy without deleting telemetry or device
                  credentials. Its QR code becomes claimable again.
                </p>
                <button
                  className="danger-button"
                  disabled={!buoy.ownerUserId || removeBuoy.isPending}
                  onClick={() =>
                    confirm(
                      `Remove ${buoy.name} from its account? Device data and credentials will be preserved.`,
                    ) && removeBuoy.mutate()
                  }
                >
                  <Trash2 />
                  {removeBuoy.isPending
                    ? "Removing…"
                    : buoy.ownerUserId
                      ? "Remove from account"
                      : "Not assigned"}
                </button>
                {removeBuoy.isError && (
                  <p className="error">{removeBuoy.error.message}</p>
                )}
              </div>
            )}
          </aside>
        </section>
      )}
    </div>
  );
}

function Tab({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: ReactNode;
  label: string;
}) {
  return (
    <button className={active ? "active" : ""} onClick={onClick}>
      {icon}
      <span>{label}</span>
    </button>
  );
}
function ControlGroup({
  title,
  icon,
  options,
  onPick,
  disabled,
}: {
  title: string;
  icon: ReactNode;
  options: string[];
  onPick: (value: string) => void;
  disabled: boolean;
}) {
  return (
    <div className="card control-group">
      <div>
        {icon}
        <strong>{title}</strong>
      </div>
      <div className="segmented">
        {options.map((option) => (
          <button
            key={option}
            disabled={disabled}
            onClick={() => onPick(option)}
          >
            {option}
          </button>
        ))}
      </div>
    </div>
  );
}
function StatusCard({
  icon,
  label,
  value,
  detail,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  detail: string;
}) {
  return (
    <div className="status-card">
      <div className="status-card-icon">{icon}</div>
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{detail}</small>
    </div>
  );
}

function QuickStatus({
  icon,
  label,
  value,
  detail,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  detail: string;
}) {
  return (
    <div className="quick-status">
      <div className="quick-status-icon">{icon}</div>
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{detail}</small>
    </div>
  );
}

function AdminActions({ buoy }: { buoy: Buoy }) {
  const reset = useMutation({ mutationFn: () => api.resetClaim(buoy.id) });
  const rotate = useMutation({ mutationFn: () => api.rotateMqtt(buoy.id) });
  return (
    <div className="admin-actions">
      <h3>Privileged actions</h3>
      <p className="muted">
        Results contain one-time credentials. Save them before leaving this
        page.
      </p>
      <div>
        <button
          className="danger-button"
          onClick={() =>
            confirm("Clear ownership and create a fresh claim code?") &&
            reset.mutate()
          }
        >
          Reset ownership
        </button>
        <button
          className="secondary-button"
          onClick={() =>
            confirm(
              "Rotate this device MQTT password? The physical config must be updated.",
            ) && rotate.mutate()
          }
        >
          Rotate MQTT credential
        </button>
      </div>
      {reset.data && (
        <SecretResult label="New claim code" value={reset.data.claimCode} />
      )}
      {rotate.data && (
        <SecretResult label="New MQTT password" value={rotate.data.password} />
      )}
    </div>
  );
}
function SecretResult({ label, value }: { label: string; value: string }) {
  return (
    <div className="secret-result">
      <span>{label} — shown once</span>
      <code>{value}</code>
    </div>
  );
}

function UsersPage() {
  const [search, setSearch] = useState("");
  const query = useQuery({
    queryKey: ["admin-users", search],
    queryFn: () => api.adminUsers(search),
  });
  return (
    <>
      <PageHeader
        eyebrow="Administration"
        title="Users"
        action={
          <input
            className="search-input"
            aria-label="Search users"
            placeholder="Search name or email"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        }
      />
      <section className="card table-card">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Joined</th>
            </tr>
          </thead>
          <tbody>
            {query.data?.items.map((user) => (
              <tr key={user.id}>
                <td>{user.name}</td>
                <td>{user.email}</td>
                <td>
                  <span className="role-pill">{user.role}</span>
                </td>
                <td>{new Date(user.createdAt).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!query.isLoading && !query.data?.items.length && (
          <p className="muted">No users found.</p>
        )}
      </section>
    </>
  );
}

function AuditPage() {
  const [search, setSearch] = useState("");
  const query = useQuery({
    queryKey: ["admin-audit", search],
    queryFn: () => api.adminAudit(search),
  });
  return (
    <>
      <PageHeader
        eyebrow="Security & operations"
        title="Audit history"
        action={
          <input
            className="search-input"
            aria-label="Search audit history"
            placeholder="Search action or target"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        }
      />
      <section className="card table-card">
        <table>
          <thead>
            <tr>
              <th>When</th>
              <th>Action</th>
              <th>Target</th>
              <th>Actor</th>
            </tr>
          </thead>
          <tbody>
            {query.data?.items.map((entry) => (
              <tr key={entry.id}>
                <td>{new Date(entry.createdAt).toLocaleString()}</td>
                <td>
                  <code>{entry.action}</code>
                </td>
                <td>
                  {entry.targetType}
                  {entry.targetId ? ` · ${entry.targetId}` : ""}
                </td>
                <td>{entry.actorUserId ?? "system"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!query.isLoading && !query.data?.items.length && (
          <p className="muted">No audit entries found.</p>
        )}
      </section>
    </>
  );
}

function AccountPage({ user }: { user: AppUser }) {
  const { mode, setMode } = useTheme();
  const [deleteError, setDeleteError] = useState("");
  const removeAccount = async () => {
    const password = window.prompt(
      "Enter your password to permanently delete this account. Social-only accounts receive a confirmation email.",
    );
    if (password === null) return;
    const result = await authClient.deleteUser(
      password ? { password } : { callbackURL: "/" },
    );
    if (result.error)
      setDeleteError(result.error.message ?? "Account deletion failed");
  };
  return (
    <>
      <PageHeader eyebrow="Personal settings" title={t("account")} />
      <section className="card account-card">
        <div className="account-profile">
          <div className="avatar large">
            {user.name.slice(0, 1).toUpperCase()}
          </div>
          <div>
            <h2>{user.name}</h2>
            <p>{user.email}</p>
          </div>
        </div>
        <div className="setting-row">
          <div>
            <h3>{t("theme")}</h3>
            <p className="muted">
              System follows the appearance selected on this device.
            </p>
          </div>
          <div className="theme-picker">
            {(["system", "light", "dark"] as ThemeMode[]).map((value) => (
              <button
                key={value}
                className={mode === value ? "active" : ""}
                onClick={() => setMode(value)}
              >
                {value === "system" ? (
                  <Settings />
                ) : value === "light" ? (
                  <Sun />
                ) : (
                  <Moon />
                )}
                {t(value)}
              </button>
            ))}
          </div>
        </div>
        <div className="danger-zone">
          <div>
            <h3>Delete account</h3>
            <p className="muted">
              You must remove every buoy from your account first. Deletion is
              permanent.
            </p>
          </div>
          <button
            className="danger-button"
            onClick={() => void removeAccount()}
          >
            Delete account
          </button>
        </div>
        {deleteError && <p className="error">{deleteError}</p>}
      </section>
    </>
  );
}

function Empty({
  icon,
  title,
  detail,
  action,
}: {
  icon: ReactNode;
  title: string;
  detail: string;
  action?: ReactNode;
}) {
  return (
    <section className="empty-state">
      <div>{icon}</div>
      <h2>{title}</h2>
      <p>{detail}</p>
      {action}
    </section>
  );
}
function CardGridSkeleton() {
  return (
    <section className="buoy-grid" aria-label="Loading">
      <div className="skeleton" />
      <div className="skeleton" />
      <div className="skeleton" />
    </section>
  );
}
function formatRelative(value: string) {
  const seconds = Math.max(
    0,
    Math.floor((Date.now() - new Date(value).getTime()) / 1000),
  );
  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} hr ago`;
  return new Date(value).toLocaleString();
}
function latestPoint(state: Buoy["state"]): TrackPoint | undefined {
  return state?.latitude != null && state.longitude != null
    ? {
        latitude: Number(state.latitude),
        longitude: Number(state.longitude),
        recordedAt: state.lastGpsFixAt ?? state.updatedAt,
        satellitesVisible: state.satellitesVisible,
      }
    : undefined;
}
